import { useState, useCallback } from 'react';
import { ChevronDown, Shield, Users, Crown, Rocket, UserPlus, PiggyBank, Heart, UserMinus, Globe } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import Modal from './Modal';

interface Service {
  title: string;
  description: string[];
  icon: LucideIcon;
}

interface ProfessionalsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const services: Service[] = [
  {
    title: 'Audit et conformité RH',
    icon: Shield,
    description: [
      'Vérification des contrats de travail et des obligations légales.',
      'Analyse des processus internes (gestion des congés, temps de travail, disciplinaire…).',
      'Recommandations sur les mises en conformité et amélioration des pratiques RH.'
    ]
  },
  {
    title: 'Accompagnement en gestion des conflits',
    icon: Users,
    description: [
      'Analyse de la situation et des enjeux humains et juridiques.',
      'Mise en place de solutions de dialogue et de médiation.',
      'Accompagnement des dirigeants et RH dans la gestion de conflits internes.',
      'Formation aux bonnes pratiques de gestion des tensions en entreprise.'
    ]
  },
  {
    title: 'Formation des managers',
    icon: Crown,
    description: [
      'Formation sur le management positif et la motivation des collaborateurs.',
      'Apprentissage des techniques de communication et gestion de conflits.',
      'Accompagnement à la délégation et au développement des talents.',
      'Sensibilisation aux obligations légales et aux bonnes pratiques RH.'
    ]
  },
  {
    title: 'Accompagnement à la création d\'entreprise',
    icon: Rocket,
    description: [
      'Conseil sur les obligations légales liées à l\'embauche et aux statuts juridiques.',
      'Rédaction des premiers contrats de travail et mise en place des registres obligatoires.',
      'Aide à la mise en place des outils RH (logiciels, bulletins de paie, fiches de poste…).',
      'Conseil en organisation du travail et structuration des équipes.'
    ]
  },
  {
    title: 'Recrutement et sélection des talents',
    icon: UserPlus,
    description: [
      'Rédaction et diffusion de l\'annonce.',
      'Tri et présélection des CV selon les critères du poste.',
      'Organisation et réalisation des entretiens téléphoniques et/ou physiques.',
      'Conseil et accompagnement sur le choix final du candidat.'
    ]
  },
  {
    title: 'Optimisation des coûts RH',
    icon: PiggyBank,
    description: [
      'Étude des charges sociales et identification des optimisations possibles.',
      'Conseil sur les aides à l\'embauche et exonérations disponibles.',
      'Optimisation de la politique de rémunération (primes, avantages en nature, intéressement…).',
      'Stratégies pour réduire le coût du turnover et de l\'absentéisme.'
    ]
  },
  {
    title: 'Politique de qualité de vie au travail (QVT)',
    icon: Heart,
    description: [
      'Diagnostic des attentes des salariés en matière de bien-être au travail.',
      'Mise en place de mesures adaptées : télétravail, flexibilité, ergonomie des postes.',
      'Développement d\'initiatives bien-être : ateliers de sensibilisation, coaching, activités de cohésion.',
      'Suivi et évaluation de l\'impact des actions mises en place.'
    ]
  },
  {
    title: 'Gestion des départs et ruptures contractuelles',
    icon: UserMinus,
    description: [
      'Conseil sur les options possibles : rupture conventionnelle, licenciement, démission.',
      'Assistance dans la rédaction des documents et la gestion des formalités.',
      'Accompagnement pour limiter les risques de litiges et contentieux.',
      'Stratégies pour un offboarding réussi et préserver l\'image de l\'entreprise.'
    ]
  },
  {
    title: 'Mise en place d\'une politique de diversité et inclusion',
    icon: Globe,
    description: [
      'Diagnostic des pratiques en matière d\'égalité et de non-discrimination.',
      'Mise en place d\'actions concrètes pour favoriser l\'inclusion.',
      'Sensibilisation des équipes et formation aux biais inconscients.',
      'Accompagnement pour répondre aux obligations légales et aux labels diversité.'
    ]
  }
];

export default function ProfessionalsModal({ isOpen, onClose }: ProfessionalsModalProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleAccordion = useCallback((index: number) => {
    setOpenIndex(prev => prev === index ? null : index);
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Services pour Professionnels"
    >
      <div className="p-6 space-y-4">
        {services.map((service, index) => (
          <div
            key={index}
            className="border-2 border-aurore-light dark:border-warm-gray-700 rounded-xl overflow-hidden hover:border-aurore-dark transition-colors"
          >
            <button
              onClick={() => toggleAccordion(index)}
              className="w-full flex items-center justify-between p-4 bg-white dark:bg-warm-gray-900 hover:bg-aurore-light/30 dark:hover:bg-warm-gray-800 transition-colors text-left"
              aria-expanded={openIndex === index}
            >
              <div className="flex items-center space-x-3 pr-4">
                <service.icon className="w-5 h-5 text-aurore-dark dark:text-aurore-light flex-shrink-0" aria-hidden="true" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-gray-100">
                  {service.title}
                </h3>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-aurore-dark dark:text-aurore-light flex-shrink-0 transition-transform duration-300 ${
                  openIndex === index ? 'rotate-180' : ''
                }`}
                aria-hidden="true"
              />
            </button>

            <div
              className={`grid transition-all duration-500 ease-in-out ${
                openIndex === index ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
              }`}
            >
              <div className="overflow-hidden">
                <div className="p-4 bg-aurore-light/20 dark:bg-warm-gray-800/50 space-y-2">
                  {service.description.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <span className="text-aurore-dark font-bold text-lg leading-none mt-1" aria-hidden="true">✓</span>
                      <p className="text-gray-700 dark:text-warm-gray-300 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
